/**
 * useTheme, theme preference loaded from ui_preferences.theme.
 *
 * Three states: 'light' | 'dark' | 'system'. 'system' tracks the OS-level
 * prefers-color-scheme media query and reacts to changes live.
 *
 * Applies `data-theme="dark"` or `data-theme="light"` to documentElement so
 * CSS overrides under `[data-theme='dark']` in app.css pick up automatically.
 *
 * Persists via PUT /api/me/preferences. Optimistic, the local state updates
 * before the server round-trips so the UI flips instantly. If the server PUT
 * fails the toast in api.ts surfaces it; we keep the optimistic state since
 * the OS theme has already changed and reverting would be more disruptive.
 *
 * Pattern: a SessionProvider-style ThemeProvider at the root of App.tsx that
 * mounts once on app start, fetches preferences (silently failing on 401),
 * and exposes { theme, setTheme, resolvedTheme } via context. The session
 * check inside fetchPreferences will 401 on the sign-in page; that's fine,
 * we default to 'system' until auth lands.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  fetchPreferences,
  updatePreferences,
  ApiError,
  type ThemeChoice,
} from '../lib/api.ts';

type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  /** The stored choice: light, dark, or system. */
  theme: ThemeChoice;
  /** What's actually applied right now (system resolved to light or dark). */
  resolvedTheme: ResolvedTheme;
  /** Persist to ui_preferences + apply immediately. */
  setTheme: (t: ThemeChoice) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function readSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'light';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', resolved);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Per Ean's 2026-06-03 feedback: default to light for everyone. Users can flip
  // to dark or system from their profile. Previously defaulted to 'system' which
  // gave OS-dark-mode users a dark UI on first visit before they had a choice.
  const [theme, setThemeState] = useState<ThemeChoice>('light');
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(readSystemTheme);

  // Load saved preference on mount. 401s are expected on auth pages; swallow.
  useEffect(() => {
    let cancelled = false;
    fetchPreferences()
      .then((p) => {
        if (cancelled) return;
        const stored = p.ui_preferences.theme;
        if (stored === 'light' || stored === 'dark' || stored === 'system') {
          setThemeState(stored);
        }
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) return;
        // Non-401 errors are non-fatal here; theme just stays at 'system'.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Track OS-level theme changes when 'system'.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const resolvedTheme: ResolvedTheme =
    theme === 'system' ? systemTheme : theme;

  // Apply to documentElement whenever it changes.
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  const setTheme = useCallback((next: ThemeChoice) => {
    setThemeState(next);
    updatePreferences({ theme: next }).catch(() => {
      // Persist failure is silent; the OS-level apply already happened.
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx === undefined) {
    throw new Error('useTheme must be used inside <ThemeProvider>');
  }
  return ctx;
}
